import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
	CONFIGURATION_API_ENDPOINTS,
	EMPTY_CONFIGURATION,
} from '@/constants/configuration';
import { FULL_TIME_JOB_TYPE_LABEL } from '@/constants/job';
import api from '@/helpers/api';
import {
	resolveCountryCode as resolveCountryCodeFromList,
	sortFranceFirst,
} from '@/helpers/country';
import {
	mapDocumentTypeRefsByCategory,
	mapHabilitationDocumentOptions,
} from '@/helpers/documents';
import {
	groupAndSortJobDurations,
	mapApplicantExperiencesToOptions,
	mapBusinessSectorsToOptions,
	mapDegreeLevelOptionsToLabelValues,
	mapDegreeLevelsToOptions,
	mapDepartmentsToOptions,
	mapHiringReasonsToOptions,
	mapInactivatedReasonsToMap,
	mapJobContractsToOptions,
	mapJobFunctionsToOptions,
	mapJobIndustriesToOptions,
	mapJobsToOptions,
	mapJobTypesToOptions,
	mapPaRefuseReasonsToOptions,
	mapRegionsToOptions,
	mapRejectedCompanyNeedReasonsToMap,
	mapUnavailabilityReasonsToOptions,
} from '@/helpers/referential';
import { mapRemunerationPeriodicityOptions } from '@/helpers/remuneration';
import {
	RemunerationPeriodicity,
	type SalaryOptionDto,
} from '@/types/remuneration';

import type { LabelValue } from '@/types/common';
import type { Configuration } from '@/types/configuration';
import type { ICountry, INationality } from '@/types/geo';
import type { DegreeLevelOption, IJobDB, JobOption } from '@/types/job';

export const useConfigurationStore = defineStore('configuration', () => {
	const configuration = ref<Configuration>({ ...EMPTY_CONFIGURATION });
	const initialized = ref(false);

	let initPromise: Promise<void> | null = null;

	const initialize = (): Promise<void> => {
		if (initialized.value) return Promise.resolve();
		if (initPromise) return initPromise;

		initPromise = (async () => {
			const results = await Promise.allSettled(
				CONFIGURATION_API_ENDPOINTS.map(({ url }) =>
					api.request({ url, method: 'GET' })
				)
			);

			CONFIGURATION_API_ENDPOINTS.forEach(({ key }, index) => {
				const result = results[index];
				(configuration.value[key] as Configuration[typeof key]) =
					result.status === 'fulfilled' ? result.value.data : [];
			});

			initialized.value = true;
		})();

		return initPromise;
	};

	const waitInitialized = (): Promise<void> => {
		if (initialized.value) return Promise.resolve();
		return initialize();
	};

	const jobOptions = computed<JobOption[]>(() =>
		mapJobsToOptions(configuration.value.jobs ?? [])
	);

	const getJob = (jobId: string): IJobDB | undefined =>
		configuration.value.jobs?.find((job) => job.id === jobId);

	const getJobOption = (jobId: string): JobOption | undefined =>
		jobOptions.value.find((job) => job.value === jobId);

	const jobContractOptions = computed((): LabelValue[] =>
		mapJobContractsToOptions(configuration.value.jobContracts ?? [])
	);

	const getJobContract = (jobContractId: string) =>
		configuration.value.jobContracts?.find(
			(contract) => contract.id === jobContractId
		);

	const jobDurationsOptions = computed(() =>
		groupAndSortJobDurations(configuration.value.jobDurations ?? [])
	);

	const jobIndustriesOptions = computed(() =>
		mapJobIndustriesToOptions(configuration.value.jobIndustries ?? [])
	);

	const jobFunctionsOptions = computed(() =>
		mapJobFunctionsToOptions(configuration.value.jobFunctions ?? [])
	);

	const jobTypesOptions = computed(() =>
		mapJobTypesToOptions(configuration.value.jobTypes ?? [])
	);

	const defaultJobType = computed(
		() =>
			jobTypesOptions.value.find((jobType) =>
				jobType.label.toLowerCase().includes(FULL_TIME_JOB_TYPE_LABEL)
			) ?? null
	);

	const businessSectorOptions = computed(() =>
		mapBusinessSectorsToOptions(configuration.value.businessSectors ?? [])
	);

	const degreeLevels = computed<DegreeLevelOption[]>(() =>
		mapDegreeLevelsToOptions(configuration.value.degreeLevels ?? [])
	);

	const applicantDegreeOptions = computed((): LabelValue[] =>
		mapDegreeLevelOptionsToLabelValues(degreeLevels.value)
	);

	const talentplugApplicantExperience = computed((): LabelValue[] =>
		mapApplicantExperiencesToOptions(
			configuration.value.applicantExperiences ?? []
		)
	);

	const workerDocuments = computed(() =>
		mapDocumentTypeRefsByCategory(configuration.value.documentTypeRefs)
	);

	const documentTypeRefsOptions = computed(() =>
		mapHabilitationDocumentOptions(configuration.value.documentTypeRefs ?? [])
	);

	const countriesOptions = computed((): ICountry[] =>
		sortFranceFirst(configuration.value.countries ?? [])
	);

	const nationalitiesOptions = computed((): INationality[] =>
		sortFranceFirst(configuration.value.nationalities ?? [])
	);

	const resolveCountryCode = (nameOrCode: string): string =>
		resolveCountryCodeFromList(nameOrCode, configuration.value.countries ?? []);

	const departmentsOptions = computed(() =>
		mapDepartmentsToOptions(configuration.value.departments ?? [])
	);

	const regionsOptions = computed(() =>
		mapRegionsToOptions(configuration.value.regions ?? [])
	);

	const remunerationPeriodicityOptions = computed(() =>
		mapRemunerationPeriodicityOptions(
			configuration.value.remunerationPeriodicities
		)
	);

	const defaultRemunerationPeriodicity = computed(() => {
		const options = remunerationPeriodicityOptions.value;
		return (
			options.find((option) => option.value === RemunerationPeriodicity.HOURLY)
				?.value ??
			options[0]?.value ??
			RemunerationPeriodicity.HOURLY
		);
	});

	const getSalaryFromById = (id: string): SalaryOptionDto | undefined =>
		configuration.value.salaryFromOptions.find((option) => option.id === id);

	const getSalaryToById = (id: string): SalaryOptionDto | undefined =>
		configuration.value.salaryToOptions.find((option) => option.id === id);

	const unavailabilityReasonsOptions = computed(() =>
		mapUnavailabilityReasonsToOptions(
			configuration.value.unavailabilityReasons ?? []
		)
	);

	const hiringReasonOptions = computed(() =>
		mapHiringReasonsToOptions(configuration.value.hiringReasons ?? [])
	);

	const paRefuseReasonOptions = computed((): LabelValue[] =>
		mapPaRefuseReasonsToOptions(configuration.value.paRefuseReasons ?? [])
	);

	const rejectedCompanyNeedReasonsMap = computed(
		(): Map<string, string> =>
			mapRejectedCompanyNeedReasonsToMap(
				configuration.value.rejectedCompanyNeedReasons
			)
	);

	const inactivatedReasonsMap = computed(
		(): Map<string, string> =>
			mapInactivatedReasonsToMap(configuration.value.inactivatedReasons)
	);

	return {
		configuration,
		initialize,
		waitInitialized,

		jobOptions,
		getJob,
		getJobOption,
		jobContractOptions,
		getJobContract,
		jobDurationsOptions,
		jobIndustriesOptions,
		jobFunctionsOptions,
		defaultJobType,
		businessSectorOptions,

		degreeLevels,
		applicantDegreeOptions,
		talentplugApplicantExperience,

		workerDocuments,
		documentTypeRefsOptions,

		countriesOptions,
		nationalitiesOptions,
		resolveCountryCode,
		departmentsOptions,
		regionsOptions,

		remunerationPeriodicityOptions,
		defaultRemunerationPeriodicity,
		getSalaryFromById,
		getSalaryToById,

		unavailabilityReasonsOptions,
		hiringReasonOptions,
		paRefuseReasonOptions,
		rejectedCompanyNeedReasonsMap,
		inactivatedReasonsMap,
	};
});
